import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { updateTripVisibility } from '../../services/api';
import './ShareModal.css';

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    tripSlug: string;
    tripTitle: string;
    isPublic: boolean;
    isPremium: boolean;
    isOwner: boolean;
    onVisibilityUpdated: (isPublic: boolean) => void;
}

export default function ShareModal({
    isOpen,
    onClose,
    tripSlug,
    tripTitle,
    isPublic: initialIsPublic,
    isPremium,
    isOwner,
    onVisibilityUpdated
}: ShareModalProps) {
    const { t, i18n } = useTranslation();
    const [isPublic, setIsPublic] = useState(initialIsPublic);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const langCode = i18n.language.split('-')[0];
    const langPrefix = langCode === 'en' ? '' : `/${langCode}`;
    const shareUrl = `${window.location.origin}${langPrefix}/trip/${tripSlug}`;

    useEffect(() => {
        setIsPublic(initialIsPublic);
        setCopied(false);
        setError(null);
    }, [initialIsPublic, isOpen]);

    if (!isOpen) return null;

    const handleMakePublic = async () => {
        setLoading(true);
        setError(null);
        try {
            await updateTripVisibility(tripSlug, true);
            setIsPublic(true);
            onVisibilityUpdated(true);
        } catch (err: any) {
            setError(err.error || t('chat.failed_to_apply'));
        } finally {
            setLoading(false);
        }
    };

    const handleMakePrivate = async () => {
        setLoading(true);
        setError(null);
        try {
            await updateTripVisibility(tripSlug, false);
            setIsPublic(false);
            onVisibilityUpdated(false);
        } catch (err: any) {
            setError(err.error || t('chat.failed_to_apply'));
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: tripTitle,
                    url: shareUrl,
                });
            } catch (err) {
                // User cancelled or share failed
            }
        }
    };

    return (
        <div className="unlock-modal-overlay" onClick={onClose}>
            <div className="unlock-modal share-modal" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose} aria-label={t('common.close')}>&times;</button>

                <h2 className="modal-title">{t('common.share_trip')}</h2>
                <p className="modal-subtitle">{t('common.share_subtitle', { title: tripTitle })}</p>

                {!isPublic ? (
                    <div className="visibility-prompt">
                        <div className="prompt-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                        </div>
                        <h3>{t('common.trip_is_private')}</h3>
                        <p>{t('common.private_desc')}</p>

                        {error && <div className="share-error">{error}</div>}

                        <button
                            className="make-public-btn"
                            onClick={handleMakePublic}
                            disabled={loading}
                        >
                            {loading ? t('common.processing') : t('common.make_public_cta')}
                        </button>
                    </div>
                ) : (
                    <div className="share-controls">
                        <div className="share-link-box">
                            <input
                                type="text"
                                readOnly
                                value={shareUrl}
                                className="share-link-input"
                                onClick={e => (e.target as HTMLInputElement).select()}
                            />
                            <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopy}>
                                {copied ? t('common.copied') : t('common.copy')}
                            </button>
                        </div>

                        <div className="share-actions">
                            {typeof navigator.share === 'function' && (
                                <button className="native-share-btn" onClick={handleNativeShare}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                                    </svg>
                                    {t('common.share_via_app')}
                                </button>
                            )}
                        </div>

                        {isOwner && (
                            <div className="unpublish-section">
                                <button
                                    className="unpublish-btn"
                                    onClick={handleMakePrivate}
                                    disabled={loading}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                    {loading ? t('common.processing') : t('common.stop_sharing_cta')}
                                </button>
                                {!isPremium && (
                                    <p className="premium-hint">{t('common.premium_visibility_hint')}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
